import { useState } from "react";
import { toast } from "sonner";
import { Loader2, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { translateError } from "@/i18n/errors";
import { useMoney } from "@/components/subscriptions/SubscriberCard";

const SOURCES = ["hosting", "minisite", "other"];
const TONE = { hosting: "text-[var(--gold-bright)]", minisite: "text-emerald-300", other: "text-sky-300", total: "text-white" };

const Line = ({ label, value, strong, testId }) => (
  <div className={`flex min-w-0 flex-wrap items-baseline justify-between gap-x-2 text-xs ${strong ? "font-bold text-emerald-200" : "text-neutral-300"}`}>
    <span className="min-w-0 break-words">{label}</span><span className="shrink-0 tabular-nums" data-testid={testId}>{value}</span>
  </div>
);

const Money = ({ cur, m, id }) => {
  const { t } = useI18n();
  const money = useMoney();
  const f = (v) => money(v, cur);
  return (
    <div className="min-w-0 space-y-1 border-t border-neutral-800 pt-2" data-testid={`${id}-${cur}`}>
      <p className="text-[0.65rem] font-semibold uppercase tracking-wider text-neutral-500">{cur.toUpperCase()} · {t("rev.payments", { n: m.count })}</p>
      <Line label={t("rev.gross")} value={f(m.gross)} testId={`${id}-${cur}-gross`} />
      {m.refunds > 0 && <Line label={`− ${t("rev.refunds")}`} value={f(m.refunds)} testId={`${id}-${cur}-refunds`} />}
      <Line label={`− ${t("rev.vat")}`} value={f(m.vat)} testId={`${id}-${cur}-vat`} />
      <Line label={`− ${t("rev.feesReal")}`} value={f(m.fees_real)} testId={`${id}-${cur}-fees-real`} />
      {m.fees_est > 0 && <Line label={`− ${t("rev.feesEst")}`} value={f(m.fees_est)} testId={`${id}-${cur}-fees-est`} />}
      <Line label={t(m.estimated ? "rev.netEstimated" : "rev.net")} value={f(m.net)} strong testId={`${id}-${cur}-net`} />
    </div>
  );
};

const Counts = ({ c, id }) => {
  const { t, fmtNum } = useI18n();
  return (
    <dl className="grid grid-cols-2 gap-2 text-xs" data-testid={`${id}-counts`}>
      {["active", "new", "cancelled", "unpaid", ...(c.vip != null ? ["vip"] : [])].map((k) => (
        <div key={k} className="min-w-0 rounded-lg bg-black/30 px-2 py-1.5">
          <dt className="break-words text-neutral-500">{t(`rev.${k}`)}</dt>
          <dd className="font-display text-sm font-bold text-white" data-testid={`${id}-${k}`}>{fmtNum(c[k])}</dd>
        </div>
      ))}
    </dl>
  );
};

const SourceCard = ({ src, money, counts }) => {
  const { t } = useI18n();
  const id = `rev-${src}`;
  const curs = Object.keys(money || {});
  return (
    <li className="card-carbon min-w-0 space-y-3 rounded-2xl p-4" data-testid={id}>
      <h3 className={`break-words font-display text-sm font-bold uppercase tracking-wider ${TONE[src]}`}>{t(src === "total" ? "rev.total" : `rev.src.${src}`)}</h3>
      <Counts c={counts} id={id} />
      {counts.trials && (
        <dl className="grid grid-cols-3 gap-2 text-xs" data-testid={`${id}-trials`}>
          {["trialing", "trial_expired", "converted"].map((k) => (
            <div key={k} className="min-w-0 rounded-lg bg-emerald-950/40 px-2 py-1.5">
              <dt className="break-words text-emerald-300/80">{t(`trial.count.${k}`)}</dt>
              <dd className="font-display text-sm font-bold text-white" data-testid={`${id}-trials-${k}`}>{counts.trials[k]}</dd>
            </div>
          ))}
        </dl>
      )}
      {counts.groups && (
        <p className="break-words rounded-lg bg-emerald-950/40 px-2 py-1.5 text-xs text-emerald-200" data-testid={`${id}-groups`}>
          {t("group.revCount", { n: counts.groups.count, sites: counts.groups.sites })}
        </p>
      )}
      {curs.length ? curs.map((c) => <Money key={c} cur={c} m={money[c]} id={id} />) : <p className="text-xs text-neutral-500">{t("rev.empty")}</p>}
    </li>
  );
};

const Products = ({ products }) => {
  const { t } = useI18n();
  const [busy, setBusy] = useState(null);
  if (!products.length) return null;
  const map = async (p, source) => {
    setBusy(p.id);
    try {
      await api.put(`/admin/revenue/products/${p.id}`, { source });
    } catch (err) {
      toast.error(translateError(err, t));
    } finally {
      setBusy(null);
    }
  };
  return (
    <div className="min-w-0 space-y-2" data-testid="rev-products">
      <p className="text-xs font-semibold uppercase tracking-wider text-neutral-400">{t("rev.products")}</p>
      <p className="break-words text-xs text-neutral-500">{t("rev.productsHint")}</p>
      <ul className="grid min-w-0 gap-2 md:grid-cols-2">
        {products.map((p) => (
          <li key={p.id} className="flex min-w-0 flex-col gap-2 rounded-xl border border-neutral-800 p-3" data-testid={`rev-product-${p.id}`}>
            <span className="min-w-0 break-words text-sm text-white [overflow-wrap:anywhere]">{p.name}{p.locked && <span className="ms-2 text-xs text-[var(--gold-bright)]">· {t("rev.ours")}</span>}
              <span className="block text-[0.65rem] text-neutral-500">{p.id}{p.created ? ` · ${new Date(p.created * 1000).toLocaleDateString()}` : ""}</span></span>
            {!p.locked && (
              <div className="flex flex-wrap gap-1.5">
                {SOURCES.map((s) => (
                  <button key={s} type="button" disabled={busy === p.id} aria-pressed={p.source === s} onClick={() => map(p, s)}
                    className={`max-w-full whitespace-normal break-words rounded-full border px-2.5 py-1 text-start text-[0.7rem] font-semibold transition-colors ${p.source === s ? "border-[var(--gold)] bg-[var(--gold)] text-black" : "border-neutral-600 text-neutral-200 hover:border-[var(--gold)]"}`}
                    data-testid={`rev-product-${p.id}-${s}`}>
                    {t(`rev.src.${s}`)}
                  </button>
                ))}
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};

export const RevenueSources = ({ rev }) => {
  const { t, locale } = useI18n();
  const [syncing, setSyncing] = useState(false);
  if (!rev) return null;
  const month = rev.months.find((m) => m.month === rev.current_month) || { sources: {}, total: {} };
  const sync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/admin/revenue/sync");
      if (data.ok) toast.success(t("rev.synced"));
      else toast.error(data.error || data.skipped || "—");
    } catch (err) {
      toast.error(translateError(err, t));
    } finally {
      setSyncing(false);
    }
  };
  return (
    <section className="card-carbon min-w-0 space-y-4 rounded-2xl p-5" data-testid="rev-section">
      <div className="flex min-w-0 flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="break-words font-display text-base uppercase tracking-wider text-[var(--gold-bright)] md:text-lg">{t("rev.title")} · {rev.current_month}</h2>
          <p className="mt-1 max-w-3xl break-words text-xs text-neutral-400">{t("rev.subtitle")}</p>
          {rev.last_sync?.at && <p className="mt-1 text-[0.7rem] text-neutral-500" data-testid="rev-last-sync">{t("rev.lastSync", { time: new Date(rev.last_sync.at).toLocaleString(locale) })}</p>}
        </div>
        <button type="button" className="btn-gold btn-sm" disabled={syncing} onClick={sync} data-testid="rev-sync-btn">
          {syncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} {t("rev.sync")}
        </button>
      </div>
      <ul className="grid min-w-0 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {SOURCES.map((s) => <SourceCard key={s} src={s} money={month.sources[s]} counts={rev.counts[s]} />)}
        <SourceCard src="total" money={month.total} counts={rev.counts.total} />
      </ul>
      <Products products={rev.products} />
    </section>
  );
};
